// Generated by Copilot- Start
import React, { ReactNode } from "react";
import DtvBox from "@directv-digital/dds.ui.dtv-box";
import DtvText from "../../../video-lab/dtv-text";
import DtvDivider from "@directv-digital/dds.ui.dtv-divider";
import PackageInfoCtaV2 from "../PackageInfoCtaV2";

export interface PackageEntry {
  id: string;
  displayLabel: string;
  packageLegal?: string;
  ctaObj?: {
    cta: string;
    ctaUrl: string;
    ctaAccessibility?: string;
  };
  type: string;
}

interface ModalPackageSectionProps {
  /** Section heading text */
  heading: string;
  /** Array of package entries to display */
  packages: PackageEntry[];
  /** Channel name for analytics */
  channelName: string;
  /** Channel category for analytics (e.g., 'rsn', 'local', 'channel') */
  channelCategory: string;
  /** Whether to show divider before this section */
  showDivider?: boolean;
  /** Top margin when no divider (default: '26px') */
  topMargin?: string;
  /** Bottom margin for heading (default: '16px') */
  headingBottomMargin?: string;
  /** Optional content to render after heading (e.g., legal text) */
  afterHeading?: ReactNode;
  /** Optional content to render before package list (e.g., legal text) */
  beforePackages?: ReactNode;
  /** Optional content to render after package list */
  afterPackages?: ReactNode;
  /** Custom heading styles */
  headingSx?: Record<string, any>;
  /** ID for the container */
  id?: string;
  /** Custom gap between package items (default: '12px') */
  packageGap?: string;
  /** Custom divider styles */
  dividerSx?: Record<string, any>;
}

/**
 * Reusable modal package section component.
 * Displays a heading, optional slots for custom content, and a list of packages with CTAs.
 *
 * Used by: RsnChannelModalV2, LocalChannelModalV2, ChannelModalV2
 */
const ModalPackageSection: React.FC<ModalPackageSectionProps> = ({
  heading,
  packages,
  channelName,
  channelCategory,
  showDivider = false,
  topMargin = "26px",
  headingBottomMargin = "16px",
  afterHeading,
  beforePackages,
  afterPackages,
  headingSx = {},
  id,
  packageGap = "12px",
  dividerSx = {}
}) => {
  if (packages.length === 0) {
    return null;
  }

  return (
    <DtvBox
      id={id}
      sx={{
        mt: showDivider ? "0px" : topMargin,
        display: "flex",
        flexDirection: "column"
      }}
    >
      {/* Optional divider */}
      {showDivider && <DtvDivider sx={{
 my: "16px",
...dividerSx
}} />}

      {/* Section heading */}
      <DtvText
        content={heading}
        variant="subhead2"
        sx={{
          fontSize: "20px !important",
          mb: afterHeading || beforePackages ? "0px" : headingBottomMargin,
          ...headingSx
        }}
      />

      {/* After heading slot (e.g., legal text for RSN tier 1) */}
      {afterHeading}

      {/* Before packages slot (e.g., legal text for RSN tier 2/3) */}
      {beforePackages}

      {/* Package list with CTAs */}
      <DtvBox
        sx={{
          display: "flex",
          gap: packageGap,
          flexDirection: "column"
          // mt: afterHeading || beforePackages ? "16px" : headingBottomMargin,
        }}
      >
        {packages.map((pkg, index) => (
          <PackageInfoCtaV2
            channelCategory={channelCategory}
            channelName={channelName}
            index={index}
            key={`${pkg.id}-${index}`}
            packageEntry={{
              id: pkg.id,
              displayLabel: pkg.displayLabel,
              packageLegal: pkg.packageLegal || "",
              ctaObj: pkg.ctaObj, // Don't provide default - let undefined pass through for optional chaining
              type: pkg.type
            }}
          />
        ))}
      </DtvBox>

      {/* After packages slot */}
      {afterPackages}
    </DtvBox>
  );
};

export default React.memo(ModalPackageSection);
// Generated by Copilot- End
